import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Progress, Tag, Button, Modal, Divider } from 'antd';
import { TrophyOutlined, BookOutlined, BulbOutlined, CalculatorOutlined, InfoCircleOutlined } from '@ant-design/icons';
import RadarChart from './RadarChart';
import { 
  OCAssessmentResult, 
  RadarChartDimension 
} from '../../types/assessment';
import {
  READING_DIMENSIONS,
  THINKING_DIMENSIONS,
  MATHEMATICAL_DIMENSIONS,
  getAssessmentLevel,
  extractDimensionScores
} from '../../utils/assessmentUtils';

interface OCAssessmentDashboardProps {
  assessmentData: OCAssessmentResult;
  onDetailView?: (module: 'reading' | 'thinking' | 'mathematics') => void;
  onRetakeTest?: (module: 'reading' | 'thinking' | 'mathematics') => void;
}

const OCAssessmentDashboard: React.FC<OCAssessmentDashboardProps> = ({
  assessmentData,
  onDetailView,
  onRetakeTest,
}) => {
  const [selectedDimension, setSelectedDimension] = useState<RadarChartDimension | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const handleDimensionClick = (dimension: RadarChartDimension) => {
    setSelectedDimension(dimension);
    setModalVisible(true);
  };

  const getReadinessColor = (readiness: string) => {
    switch (readiness) {
      case 'Excellent': return '#52c41a';
      case 'Good': return '#1890ff';
      case 'Fair': return '#faad14';
      case 'Needs Improvement': return '#f5222d';
      default: return '#d9d9d9';
    }
  };

  const getProgressColor = (score: number) => {
    if (score >= 9) return '#52c41a';
    if (score >= 7) return '#1890ff';
    if (score >= 4) return '#faad14';
    return '#f5222d';
  };

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <h1 style={{ 
          fontSize: '28px', 
          fontWeight: 'bold', 
          color: '#1f2937',
          marginBottom: '8px'
        }}>
          OC Assessment Report
        </h1>
        <p style={{ fontSize: '16px', color: '#666', marginBottom: '16px' }}>
          {assessmentData.studentName} • {assessmentData.assessmentDate.toLocaleDateString()}
        </p>
        
        {/* Overall Score Card */}
        <Card style={{ 
          maxWidth: '600px', 
          margin: '0 auto',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          color: 'white'
        }}>
          <Row gutter={24} align="middle">
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <TrophyOutlined style={{ fontSize: '48px', color: '#ffd700' }} />
              </div>
            </Col>
            <Col span={16}>
              <div>
                <h2 style={{ color: 'white', margin: 0, fontSize: '24px' }}>
                  Overall Score: {assessmentData.overall.totalScore.toFixed(1)}/10
                </h2>
                <Tag 
                  color={getReadinessColor(assessmentData.overall.ocReadiness)}
                  style={{ fontSize: '14px', padding: '4px 12px', marginTop: '8px' }}
                >
                  OC Readiness: {assessmentData.overall.ocReadiness}
                </Tag>
                <Progress 
                  percent={assessmentData.overall.totalScore * 10} 
                  showInfo={false}
                  strokeColor="#ffd700"
                  trailColor="rgba(255,255,255,0.3)"
                  style={{ marginTop: '12px' }}
                />
              </div>
            </Col>
          </Row>
        </Card>
      </div>

      {/* Module Scores Summary */}
      <Row gutter={24} style={{ marginBottom: '32px' }}>
        <Col span={8}>
          <Card 
            title={
              <span>
                <BookOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                Reading Comprehension
              </span>
            }
            extra={
              <Button 
                type="link" 
                onClick={() => onDetailView?.('reading')}
                style={{ padding: 0 }}
              >
                View Details
              </Button>
            }
          >
            <Statistic
              value={assessmentData.reading.overallScore}
              suffix="/ 10"
              precision={1}
              valueStyle={{ 
                color: getProgressColor(assessmentData.reading.overallScore),
                fontSize: '24px'
              }}
            />
            <Progress 
              percent={assessmentData.reading.overallScore * 10}
              strokeColor={getProgressColor(assessmentData.reading.overallScore)}
              showInfo={false}
              style={{ marginTop: '8px' }}
            />
            <p style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
              {getAssessmentLevel(assessmentData.reading.overallScore).description}
            </p>
          </Card>
        </Col>

        <Col span={8}>
          <Card 
            title={
              <span>
                <BulbOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />
                Thinking Skills
              </span>
            }
            extra={
              <Button 
                type="link" 
                onClick={() => onDetailView?.('thinking')}
                style={{ padding: 0 }}
              >
                View Details
              </Button>
            }
          >
            <Statistic
              value={assessmentData.thinking.overallScore}
              suffix="/ 10"
              precision={1}
              valueStyle={{ 
                color: getProgressColor(assessmentData.thinking.overallScore),
                fontSize: '24px'
              }}
            />
            <Progress 
              percent={assessmentData.thinking.overallScore * 10}
              strokeColor={getProgressColor(assessmentData.thinking.overallScore)}
              showInfo={false}
              style={{ marginTop: '8px' }}
            />
            <p style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
              {getAssessmentLevel(assessmentData.thinking.overallScore).description}
            </p>
          </Card>
        </Col>

        <Col span={8}>
          <Card 
            title={
              <span>
                <CalculatorOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Mathematical Reasoning
              </span>
            }
            extra={
              <Button 
                type="link" 
                onClick={() => onDetailView?.('mathematics')}
                style={{ padding: 0 }}
              >
                View Details
              </Button>
            }
          >
            <Statistic
              value={assessmentData.mathematics.overallScore}
              suffix="/ 10"
              precision={1}
              valueStyle={{ 
                color: getProgressColor(assessmentData.mathematics.overallScore),
                fontSize: '24px'
              }}
            />
            <Progress 
              percent={assessmentData.mathematics.overallScore * 10}
              strokeColor={getProgressColor(assessmentData.mathematics.overallScore)}
              showInfo={false}
              style={{ marginTop: '8px' }}
            />
            <p style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
              {getAssessmentLevel(assessmentData.mathematics.overallScore).description}
            </p>
          </Card>
        </Col>
      </Row>

      {/* Radar Charts */}
      <Row gutter={24} style={{ marginBottom: '32px' }}>
        <Col span={8}>
          <Card title="Reading Comprehension Analysis" style={{ height: '480px' }}>
            <RadarChart
              title=""
              data={extractDimensionScores(assessmentData.reading, READING_DIMENSIONS)}
              dimensions={READING_DIMENSIONS}
              size="small"
              onDimensionClick={handleDimensionClick}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card title="Thinking Skills Analysis" style={{ height: '480px' }}>
            <RadarChart
              title=""
              data={extractDimensionScores(assessmentData.thinking, THINKING_DIMENSIONS)}
              dimensions={THINKING_DIMENSIONS}
              size="small"
              onDimensionClick={handleDimensionClick}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card title="Mathematical Reasoning Analysis" style={{ height: '480px' }}>
            <RadarChart
              title=""
              data={extractDimensionScores(assessmentData.mathematics, MATHEMATICAL_DIMENSIONS)}
              dimensions={MATHEMATICAL_DIMENSIONS}
              size="small"
              onDimensionClick={handleDimensionClick}
            />
          </Card>
        </Col>
      </Row>

      {/* Recommendations */}
      <Card 
        title={
          <span>
            <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Personalized Learning Recommendations
          </span>
        }
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={24}>
          <Col span={16}>
            <h4 style={{ color: '#1f2937', marginBottom: '12px' }}>Key Areas for Improvement:</h4>
            <ul style={{ paddingLeft: '20px' }}>
              {assessmentData.overall.recommendations.map((recommendation, index) => (
                <li key={index} style={{ marginBottom: '8px', color: '#666' }}>
                  {recommendation}
                </li>
              ))}
            </ul>
          </Col>
          <Col span={8}>
            <h4 style={{ color: '#1f2937', marginBottom: '12px' }}>Next Steps:</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Button type="primary" onClick={() => onRetakeTest?.('reading')}>
                Practice Reading
              </Button>
              <Button type="primary" onClick={() => onRetakeTest?.('thinking')}>
                Practice Thinking Skills
              </Button>
              <Button type="primary" onClick={() => onRetakeTest?.('mathematics')}>
                Practice Mathematics
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Dimension Detail Modal */}
      <Modal
        title={selectedDimension?.label}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedDimension && (
          <div>
            <h4 style={{ color: '#1f2937', marginBottom: '12px' }}>Dimension Description:</h4>
            <p style={{ color: '#666', marginBottom: '16px', lineHeight: '1.6' }}>
              {selectedDimension.description}
            </p>
            
            <Divider />
            
            <h4 style={{ color: '#1f2937', marginBottom: '12px' }}>Your Performance:</h4>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ 
                fontSize: '36px', 
                fontWeight: 'bold', 
                color: getProgressColor(assessmentData.reading[selectedDimension.key as keyof typeof assessmentData.reading] as number || 0),
                marginBottom: '8px'
              }}>
                {((assessmentData.reading[selectedDimension.key as keyof typeof assessmentData.reading] as number) || 
                  (assessmentData.thinking[selectedDimension.key as keyof typeof assessmentData.thinking] as number) ||
                  (assessmentData.mathematics[selectedDimension.key as keyof typeof assessmentData.mathematics] as number) || 0).toFixed(1)}
                <span style={{ fontSize: '18px', color: '#666' }}> / 10</span>
              </div>
              <Tag color={getAssessmentLevel(
                (assessmentData.reading[selectedDimension.key as keyof typeof assessmentData.reading] as number) || 
                (assessmentData.thinking[selectedDimension.key as keyof typeof assessmentData.thinking] as number) ||
                (assessmentData.mathematics[selectedDimension.key as keyof typeof assessmentData.mathematics] as number) || 0
              ).color}>
                {getAssessmentLevel(
                  (assessmentData.reading[selectedDimension.key as keyof typeof assessmentData.reading] as number) || 
                  (assessmentData.thinking[selectedDimension.key as keyof typeof assessmentData.thinking] as number) ||
                  (assessmentData.mathematics[selectedDimension.key as keyof typeof assessmentData.mathematics] as number) || 0
                ).label}
              </Tag>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OCAssessmentDashboard;